import { ref, computed } from 'vue'

export interface IParametroSistema {
  id: number
  clave: string
  valor: string
  tipo: 'numero' | 'texto' | 'booleano' | 'json'
  descripcion: string
  categoria: string
  actualizado_por: string | null
  updated_at: string
}

export const DEFAULT_REGLAMENTO_AO = `REGLAMENTO DE OPERACIÓN Y ACCESO — MUELLE PRAT (EPV)

1. DISPOSICIONES GENERALES
El presente reglamento rige la operación, atraque y acceso de embarcaciones y tripulaciones dentro del recinto portuario de Muelle Prat administrado por la Empresa Portuaria Valparaíso (EPV).

2. CONDICIONES OPERACIONALES Y SEGURIDAD
Toda embarcación que opere comercialmente en Muelle Prat debe contar con una Autorización de Operación (AO) vigente, otorgada por EPV, además de mantener al día su Certificado de Navegabilidad emitido por la Autoridad Marítima (DIRECTEMAR).

3. SUSPENSIÓN Y CADUCIDAD
El vencimiento del Certificado de Navegabilidad implicará la suspensión automática de la Autorización de Operación. Asimismo, EPV podrá aplicar suspensiones administrativas o cancelaciones definitivas ante incumplimientos graves al presente reglamento.

4. CONTROL DE ACCESO
El ingreso al recinto se realizará exclusivamente mediante los medios de acreditación autorizados por EPV.`

const LOCAL_STORAGE_KEY_PREFIX = 'cfg_param_'
const DEFAULT_URL = 'https://cors.biznet.cl:8080/http://muelleprat.biznet.cl/websrv/sw/configuracion_sistema.php'

class ConfiguracionSistemaServices {
  private endpointUrl: string
  public parametrosList = ref<IParametroSistema[]>([])
  public parametrosMap = ref<Record<string, string>>({
    vigencia_ao_anos: '3',
    reglamento_ao: DEFAULT_REGLAMENTO_AO,
  })
  public isLoading = ref(false)
  private hasLoaded = false

  constructor() {
    this.endpointUrl = import.meta.env.VITE_CONFIGURACION_SISTEMA_URL || DEFAULT_URL
    // Cargar caché local inmediato si existe
    try {
      const cachedReglamento = localStorage.getItem(`${LOCAL_STORAGE_KEY_PREFIX}reglamento_ao`)
      if (cachedReglamento) {
        this.parametrosMap.value.reglamento_ao = cachedReglamento
      }
      const cachedVigencia = localStorage.getItem(`${LOCAL_STORAGE_KEY_PREFIX}vigencia_ao_anos`)
      if (cachedVigencia) {
        this.parametrosMap.value.vigencia_ao_anos = cachedVigencia
      }
    } catch (_) {
      // Ignorar restricciones de localStorage en entornos restringidos
    }
    this.cargarParametros()
  }

  public async cargarParametros(): Promise<IParametroSistema[]> {
    this.isLoading.value = true
    try {
      const res = await fetch(this.endpointUrl, {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = await res.json()
      if (json.status === 'ok' && Array.isArray(json.data)) {
        this.parametrosList.value = json.data
        const map: Record<string, string> = {}
        for (const p of json.data) {
          map[p.clave] = p.valor
          try {
            localStorage.setItem(`${LOCAL_STORAGE_KEY_PREFIX}${p.clave}`, p.valor)
          } catch (_) {}
        }
        this.parametrosMap.value = { ...this.parametrosMap.value, ...map }
        this.hasLoaded = true
        return json.data
      }
    } catch (err) {
      console.warn('No se pudo cargar configuracion_sistema, usando valores por defecto/caché:', err)
    } finally {
      this.isLoading.value = false
    }
    return this.parametrosList.value
  }

  public getNumero(clave: string, defaultValue: number): number {
    const v = this.parametrosMap.value[clave]
    if (v === undefined || v === null || v === '') return defaultValue
    const n = Number(v)
    return isNaN(n) ? defaultValue : n
  }

  public getTexto(clave: string, defaultValue: string): string {
    const v = this.parametrosMap.value[clave]
    return v !== undefined && v !== null && v.trim() !== '' ? v : defaultValue
  }

  public get vigenciaAOAnos(): number {
    return this.getNumero('vigencia_ao_anos', 3)
  }

  public get reglamentoAO(): string {
    return this.getTexto('reglamento_ao', DEFAULT_REGLAMENTO_AO)
  }

  public async guardarParametros(
    params: Record<string, string | number>,
    actualizado_por: string = 'admin',
  ): Promise<boolean> {
    this.isLoading.value = true
    try {
      const res = await fetch(this.endpointUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          params,
          actualizado_por,
        }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = await res.json()
      if (json.status === 'ok') {
        // Actualizar localmente el mapa y caché
        for (const [k, v] of Object.entries(params)) {
          this.parametrosMap.value[k] = String(v)
          try {
            localStorage.setItem(`${LOCAL_STORAGE_KEY_PREFIX}${k}`, String(v))
          } catch (_) {}
        }
        await this.cargarParametros()
        return true
      }
    } catch (err) {
      console.error('Error guardando configuracion_sistema:', err)
    } finally {
      this.isLoading.value = false
    }
    return false
  }
}

export const configuracionSistemaManager = new ConfiguracionSistemaServices()
export default configuracionSistemaManager
